import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import { useSelector } from "react-redux";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = (userTheme) =>
  makeStyles(() => ({
    tag: {
      backgroundColor:
        userTheme === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)", // Theme-based background for selected tags
      color: userTheme === "dark" ? "white" : "black", // Theme-based text color
      borderRadius: "4px",
      border: `1px solid ${
        userTheme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
      }`,
      margin: "2px", 
    },
    listbox: {
      backdropFilter: "blur(10px)",
      border: `1px solid ${
        userTheme === "dark" ? "rgba(9, 9, 9, 0.66)" : "rgba(0, 0, 0, 0.3)"
      }`,
      backgroundColor:
        userTheme === "dark"
          ? "rgba(0, 0, 0, 0.7)"
          : "rgba(255, 255, 255, 0.7)", // Theme-based dropdown background
      borderRadius: "10px",
      boxShadow:
        userTheme === "dark"
          ? "0 4px 30px rgba(0, 0, 0, 0.5)"
          : "0 4px 30px rgba(0, 0, 0, 0.2)", // Lighter shadow for light mode
      color: userTheme === "dark" ? "#ffffff" : "#000000",
      "& .MuiAutocomplete-option": {
        "&[data-focus='true']": {
          backgroundColor:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.1)", // Hover color based on theme
        },
        "&[aria-selected='true']": {
          backgroundColor:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.2)", // Selected color based on theme
        },
      },
    },
    textField: {
      backdropFilter: "none",
      backgroundColor:
        userTheme === "dark" ? "rgba(0, 0, 0, 0.8)" : "#ffffff", // Theme-based input background
      borderRadius: "8px",
      "& .MuiOutlinedInput-root": {
        color: userTheme === "dark" ? "white" : "black", // Theme-based text color
        "&::placeholder": {
          color:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.5)", // Placeholder color
          opacity: 1,
        },
        "& fieldset": {
          border:'none',
          borderColor:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.3)"
              : "rgba(0, 0, 0, 0.3)", // Subtle border color
        },
        "&:hover fieldset": {
          borderColor:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.5)"
              : "rgba(0, 0, 0, 0.5)", // Brighter border on hover
        },
        "&.Mui-focused fieldset": {
          border:'none',
          borderColor:
            userTheme === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.7)", // Bright border when focused
        },
      },
    },
  }))();


export default function LimitTags({ value, onChange, data }) {

const userTheme = useSelector((state) => state.commonSlice.theme);

const theme = createTheme({
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          color: "white",
          borderRadius: "4px",
          border: "1px solid rgba(255, 255, 255, 0.3)",
        },
        deleteIcon: {
          color: userTheme=='dark'? "rgba(255, 255, 255, 0.7)":"rgba(85, 83, 83, 0.7)", // Cross button color for dark mode
          "&:hover": {
            color: "rgba(255, 255, 255, 1)", // Brighter cross button on hover
          },
        },
      },
    },
  },
});


  const classes = useStyles(userTheme);
  const [selectedClasses, setSelectedClasses] = useState([]);

  useEffect(() => {
    if (value && data) {
      const selectedIndexes = value.map((element) =>
        data.findIndex((elm) => elm._id === (element.keywordId || element._id))
      );
      setSelectedClasses(selectedIndexes);
    } else {
      setSelectedClasses([]);
    }
  }, [value, data]);

  return (
    <ThemeProvider theme={theme}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        className="custom-autocomplete"
        onChange={onChange}
        value={selectedClasses.map((index) => data[index])}
        options={data}
        classes={{
          listbox: classes.listbox,
          tag: classes.tag,
        }}
        style={{ border: "1px solid gray", borderRadius: "8px", }}
        getOptionLabel={(option) =>
          option?.name || option?.keyword || "N/A"
        }
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li
              key={key}
              {...optionProps}
              style={{
                color: userTheme === "dark" ? "white" : "black",
              }}
            >
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{
                  marginRight: 8,
                  color: userTheme === "dark" ? "white" : "black",
                }}
                checked={selected}
              />
              {option?.name || option?.keyword || "N/A"}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select Options"
            size="large"
            className={classes.textField}
          />
        )}
        componentsProps={{
          clearIndicator: {
            style: { color: userTheme === "dark" ? "white" : "black" },
          },
          popupIcon: {
            style: { color: userTheme === "dark" ? "white" : "black" },
          },
        }}
      />
    </ThemeProvider>
  );
}
